import { Property, SsqSsqHeadingSsqProperty } from "src/api/sure-api/ssq/useGetOneSsqVersion";
import {
    ElementsGroupByPosition,
    ElementsGroups,
    FormPropertyContext,
    FormPropertyInitialValues,
    PropertyUpdateHandler,
    ScoreProperty,
    ValidationState,
} from "./context";
import { DimensionValue } from "src/api/client-api/olapScores/olapScoresSchema";
import { ElementsMapping } from "../../context/context";
import { useState } from "react";
import { MenuItemType } from "src/components/ActionsPopover/ActionsPopover.types";
import { usePropertyComment } from "./hooks/usePropertyComment";
import { usePropertyAttachment } from "./hooks/usePropertyAttachment";
import { Post, PostElement } from "src/api/client-api/post/postSchema";
import { DeleteProperty } from "src/api/client-api/property/useDeleteProperty";
import { ValidationMessagesByKey, ValidationSeverity } from "../validationTypes";
import { DataListValueOverride } from "src/api/client-api/dataListValueOverride/useGetAllDataListValueOverride";
import { buildPropertyValidationKey } from "src/utils/validation";

type Props = {
    children: ReactNode;
    post: Post;
    elementsByPropertyId: ElementsMapping;
    onUpdate: PropertyUpdateHandler;
    onDelete: (data: DeleteProperty) => void;

    allowComments: boolean;
    scoreUnit: string;

    ssqSsqHeadingSsqProperty: SsqSsqHeadingSsqProperty;
    ssqVersionStructureId?: number | null;
    score: ScoreProperty;
    scoreDimension: {
        value?: DimensionValue;
        grade?: DimensionValue;
    };
    canEdit: boolean;
    changePost: () => void;
    validationErrors?: ValidationMessagesByKey;
    dataListValueOverrides: DataListValueOverride[];
};
export const FormPropertyProvider = (props: Props) => {
    const {
        dataListValueOverrides,
        validationErrors,
        canEdit,
        changePost,
        ssqSsqHeadingSsqProperty,
        ssqVersionStructureId,
        score,
        allowComments,
        elementsByPropertyId,
        post,
        onDelete,
        onUpdate,
    } = props;

    const [rowMenuAnchor, setRowMenuAnchor] = useState<null | HTMLElement>(null);
    const [menuItems, setMenuItems] = useState<MenuItemType[]>([]);

    const attachmentState = usePropertyAttachment({
        changePost,
    });
    const commentState = usePropertyComment();

    // const children = ssqSsqHeadingSsqProperty.children ?? [];
    // const propertyOverrides = children.reduce<PropertyOverrideMapping>((acc, child) => {
    //     acc[child.ssqProperty.id] = child;
    //     return acc;
    // }, {});

    const getValidationError = (propertyId: number, position: number): ValidationState => {
        const sharedPropertyValidationKey = buildPropertyValidationKey(propertyId, position);
        const occurrenceValidationKey = buildPropertyValidationKey(propertyId, position, ssqVersionStructureId);
        let propertyValidationKey = sharedPropertyValidationKey;
        if (validationErrors?.[occurrenceValidationKey] !== undefined) {
            propertyValidationKey = occurrenceValidationKey;
        }
        const validationMessages = validationErrors?.[propertyValidationKey] ?? [];
        const hasValidationMessages = validationMessages.length > 0;
        const hasError = validationMessages.some((validationMessage) => validationMessage.severity === "error");
        const validationSeverity: ValidationSeverity | undefined =
            hasError ? "error"
            : hasValidationMessages ? "warning"
            : undefined;

        return { propertyValidationKey, validationSeverity, validationMessages, hasValidationMessages };
    };

    function getElement(propertyId: number, position: number): PostElement | undefined;
    function getElement(propertyId: number): PostElement[];
    function getElement(propertyId: number, position?: number): PostElement[] | PostElement | undefined {
        const normalizedSsqVersionStructureId = ssqVersionStructureId ?? undefined;
        const elements = (elementsByPropertyId[propertyId] ?? []).filter(
            (element) => element.ssqVersionStructureId === normalizedSsqVersionStructureId,
        );

        if (position !== undefined) {
            const element = elements.find((elem) => elem.position === position);
            return element;
        }

        return elements;
    }

    const getElementsGroups = (properties: Property[]): ElementsGroups => {
        // We shouldn't need this because:
        // - We need it ONLY to be able to fill properties in an optimistic way; (why not)
        // - We need it to PREFILL all the default values on the front.
        // - BUT, the Backend already prefills all the defaults on its own.
        let maxPosition = 0;
        const elementsGroupByPosition = properties.reduce<ElementsGroupByPosition>((acc, oneProp) => {
            const allPositionsForElement = getElement(oneProp.id);
            const newAcc = allPositionsForElement.reduce((innerAcc, elem) => {
                const elemPosition = elem.position;
                if (elemPosition > maxPosition) {
                    maxPosition = elemPosition;
                }
                if (innerAcc[elemPosition] === undefined) {
                    innerAcc[elemPosition] = [];
                }
                innerAcc[elemPosition].push(elem);

                return innerAcc;
            }, acc);
            return newAcc;
        }, {});

        return {
            elementsGroupByPosition,
            maxPosition,
        };
    };

    const scoreUnit = props.scoreUnit.replaceAll(" ", "\u00A0");
    const items: FormPropertyInitialValues = {
        attachmentState,
        commentState,

        rowMenuAnchor: post.canAnswerSsq ? rowMenuAnchor : null,
        setRowMenuAnchor,
        menuItems: post.canAnswerSsq ? menuItems : [],
        setMenuItems,
        dataListValueOverrides,

        getElement,
        getElementsGroups,

        ssqSsqHeadingSsqProperty,
        ssqVersionStructureId,
        post,
        allowComments,
        elementsByPropertyId,
        onUpdate,
        onDelete,

        score,
        scoreUnit,
        changePost,
        canEdit,
        getValidationError,
    };

    return <FormPropertyContext.Provider value={items}>{props.children}</FormPropertyContext.Provider>;
};
